/**
 * PHI-safe crash context for issue #1376 and future uncaught-error diagnosis.
 *
 * The reported crash only reproduces with the QuillBot browser extension enabled,
 * which injects its own DOM into the page. To confirm/deny extension involvement
 * from production reports, we snapshot *structural* signals of extension-injected
 * DOM at crash time — tag names and attribute names only, never text content, so
 * no message body / PHI is ever sent to Sentry.
 */

const EXTENSION_ATTR_PATTERN = /quillbot|grammarly|qb-|gr-|data-gr|lt-|sapling|wordtune/i;
const MAX_ITEMS = 30;

export interface ICrashContext {
  hasExtensionDom: boolean;
  extensionCustomTags: string[];
  extensionAttrs: string[];
  route: string;
  activeElementTag: string | null;
}

/**
 * Scans the live DOM for signals that a browser extension has injected nodes.
 * Custom-element tags (a hyphen in the tag name) and known extension attribute
 * markers are strong indicators. Returns names only — no text/values.
 */
export function collectCrashContext(): ICrashContext {
  const customTags = new Set<string>();
  const attrs = new Set<string>();

  try {
    const all = document.getElementsByTagName("*");
    for (let i = 0; i < all.length; i++) {
      const el = all[i];
      const tag = el.tagName.toLowerCase();
      if (tag.includes("-")) {
        customTags.add(tag);
      }
      const names = el.getAttributeNames?.() ?? [];
      for (const name of names) {
        if (EXTENSION_ATTR_PATTERN.test(name)) {
          attrs.add(name);
        }
      }
      if (customTags.size >= MAX_ITEMS && attrs.size >= MAX_ITEMS) {
        break;
      }
    }
  } catch {
    // best-effort only; never let context collection mask the real error
  }

  let activeElementTag: string | null = null;
  try {
    activeElementTag = document.activeElement?.tagName.toLowerCase() ?? null;
  } catch {
    activeElementTag = null;
  }

  const extensionCustomTags = [...customTags].slice(0, MAX_ITEMS);
  const extensionAttrs = [...attrs].slice(0, MAX_ITEMS);

  return {
    hasExtensionDom:
      extensionCustomTags.length > 0 || extensionAttrs.length > 0,
    extensionCustomTags,
    extensionAttrs,
    route: typeof window !== "undefined" ? window.location.pathname : "",
    activeElementTag,
  };
}
